import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service, ValidationErrors } from 'webapp-types';
import {
	DeliveryTimeDateRangeType,
	DeliveryTimeType,
	GranulationTypeId,
	type DeliveryType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { createCommaDelimitedStringWithAnd, createDeliveryTypesArray } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';
import NotificationsDeliveryTargetsComponent from 'webapp/components/notifications/delivery-targets';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import NotificationsEditFormMetricSetupRowComponent from 'webapp/components/notifications/edit/form/metric-setup-row';
import FormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';

import type { MetricSetup, Scorecard } from 'webapp/models-custom';
import DsCompare from 'webapp/models-custom/ds-compare';
import type ScorecardRecord from 'webapp/models/scorecard';

import type { DeliveryTimeRangeOption } from 'webapp/services/model-manager/metric-digests';

type ValidationKeys = 'delivery' | 'metric' | 'unit' | 'attribute';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: Scorecard;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

export default class NotificationsEditFormDigestMainComponent extends Component<MainComponentInterface> {
	@service('constants') declare constantsService: Service.Constants;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/metric-digests')
	declare mmMetricDigestsService: Service.ModelManager.MetricDigests;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;

	@tracked selectedDeliveryTimeType: DeliveryTimeType | null = null;
	@tracked selectedDeliveryTimeDateRangeType: DeliveryTimeDateRangeType;

	@tracked model: Scorecard; // tracking here to detect changes

	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.model = this.args.model;

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);

		this.setupNewDataSelect();

		this.selectedDeliveryTimeDateRangeType = this.getDeliveryTimeDateRangeType();
		this.selectedDeliveryTimeType = this.getDeliveryTimeType();
	}

	get dataSelect() {
		return this.model.temporaryEditDataSelects[0]!;
	}

	get metricSetup(): MetricSetup {
		return this.dataSelect.metricSetup;
	}

	get isDuplicate(): boolean {
		return !!this.model.cloneFrom;
	}

	get hasDefaultSettings(): boolean {
		return this.model.isNew && !this.isDuplicate;
	}

	get scheduleDayOfWeek(): string {
		return String(this.model.scheduleDayOfWeek?.[0]);
	}

	get deliveryTimeOptions() {
		return this.mmMetricDigestsService.defaultMetricDigestOptions;
	}

	get selectedDeliveryTimeRangeOption() {
		return (
			this.deliveryTimeRangeOptions.find(
				(item) => item.value === this.selectedDeliveryTimeDateRangeType,
			) || this.deliveryTimeRangeOptions[0]!
		);
	}

	@cached
	get deliveryTimeRangeOptions() {
		return this.mmMetricDigestsService.deliveryTimeRangeOptions;
	}

	@action
	updateDeliveryTargets(delivery: DeliveryType[], isFromSetDefaults = false) {
		this.model.delivery = delivery;

		if (isFromSetDefaults) {
			next(() => {
				this.args.model.applyBufferedChanges(['delivery']);
			});
		}

		this.args.resetErrorData?.('delivery');
	}

	@action
	changeDaysOfWeek(day: string | null) {
		this.model.scheduleDayOfWeek = [Number(day!)];
		this.selectedDeliveryTimeType = this.getDeliveryTimeType();
	}

	@action
	changeHourOfDay(value: number) {
		this.model.scheduleHourOfDay = value;
		this.selectedDeliveryTimeType = this.getDeliveryTimeType();
	}

	@action
	changeDeliveryTimeDateRange(value: DeliveryTimeDateRangeType | null) {
		this.selectedDeliveryTimeDateRangeType = value || DeliveryTimeDateRangeType.LAST_WEEK;
		this.selectedDeliveryTimeType = this.getDeliveryTimeType();
	}

	@action
	changeDeliveryTimeType(deliveryTimeType: AnySimpleValue | null) {
		const metricDigest = this.model,
			deliveryTimeOption = this.deliveryTimeOptions.find((deliveryTimeOption) => {
				return deliveryTimeOption.value === deliveryTimeType;
			})!;

		metricDigest.scheduleDayOfWeek = deliveryTimeOption.scheduledDays;
		metricDigest.scheduleHourOfDay = deliveryTimeOption.scheduledHour;
		this.changeDeliveryTimeDateRange(deliveryTimeOption.deliveryTimeRange);

		this.selectedDeliveryTimeType = deliveryTimeType as DeliveryTimeType;
	}

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const model = this.model;

		model.validate(); // to cleanup general errors

		if (!model.delivery.length) {
			errors.delivery = [{ message: 'Required' }];
		}

		const dataSelect = this.dataSelect;
		if (dataSelect.metricSetupKey) {
			if (!dataSelect.metricSetup.isFilledSingleOutput) {
				errors.attribute = [{ message: 'Required' }];
				errors.unit = [{ message: 'Required' }];
			}
		} else {
			errors.metric = [{ message: 'Required' }];
		}

		return errors;
	}

	@action
	save() {
		const model = this.model,
			isNew = model.isNew,
			isCopy = isNew && model.cloneFrom,
			selectedDateRangeCopy = this.selectedDeliveryTimeRangeOption.dateRange.createCopy();

		selectedDateRangeCopy.updateFromPredefinedGranulationId(GranulationTypeId.TOTAL);

		let compares: DSSetup.Compare[] = [];
		if (!selectedDateRangeCopy.isAllTime && !selectedDateRangeCopy.isSince) {
			const compare = DsCompare.createFromFormula(null);
			compare.updateFromTypeId(DsCompare.customPreviousId, selectedDateRangeCopy);
			if (compare.formula) {
				compares = [compare.formula];
			}
		}

		let dataSelect = this.dataSelect;

		model.dateRange = selectedDateRangeCopy.formula;
		model.title = this.metricSetup.selectedMetricTitle;
		return model.save().then((scorecardRecord: ScorecardRecord) => {
			dataSelect.blockId = scorecardRecord.blockId!;
			dataSelect.dateRange = selectedDateRangeCopy.formula;
			dataSelect.compares = compares;

			const trackingEvent = isNew
				? TrackingEvent.METRIC_DIGEST_CREATED
				: TrackingEvent.METRIC_DIGEST_EDITED;

			if (isCopy) {
				const dsData = dataSelect.toJSON();
				dataSelect = this.mmDataSelectsService.createNewModel(dsData);
			}

			return dataSelect.save().then(() => {
				// to "mark it as clean"
				model.temporaryEditDataSelects = [
					this.mmDataSelectsService.createModelFromRecord(dataSelect.model),
				];

				const deliveryTypes = createDeliveryTypesArray(model.delivery),
					deliveryDay = this.constantsService.daysOfWeek.find(
						(item) => item.value === model.scheduleDayOfWeek[0],
					)?.label,
					trackingData = {
						origin: this.args.origin,
						delivery: createCommaDelimitedStringWithAnd(deliveryTypes, true),
						deliveryDay,
						datasource: this.metricSetup.sourceType?.key,
						metrics: this.metricSetup.selectedMetricTitle,
					};
				this.analyticsService.event(trackingEvent, trackingData);
			});
		});
	}

	@action
	checkModelChange() {
		const oldModel = this.model;
		const newModel = this.args.model;
		if (oldModel !== newModel) {
			this.model = newModel;
			this.setupNewDataSelect();
			// re-sync the local delivery-time state, otherwise switching between
			// notifications keeps the previously edited one's Frequency / Data from
			this.selectedDeliveryTimeDateRangeType = this.getDeliveryTimeDateRangeType();
			this.selectedDeliveryTimeType = this.getDeliveryTimeType();
			oldModel.cleanupTemporaryEditDataSelects();
		}
	}

	private setupNewDataSelect() {
		const savedDataSelect = this.model.savedDataSelects[0];

		// this.dataSelect is used just for metricSetup and not for daterange etc
		const dataSelect = savedDataSelect
			? this.mmDataSelectsService.createModelFromRecord(savedDataSelect.model)
			: this.mmScorecardsService.createDataSelectForScorecard(this.model);

		this.model.temporaryEditDataSelects = [dataSelect];
	}

	private getDeliveryTimeType(): DeliveryTimeType {
		const metricDigest = this.model,
			deliveryTimeOption = this.deliveryTimeOptions.find((deliveryTimeOption) => {
				return deliveryTimeOption.isEqualSchedule(
					metricDigest,
					this.selectedDeliveryTimeDateRangeType,
				);
			});

		return deliveryTimeOption ? deliveryTimeOption.value : DeliveryTimeType.CUSTOM;
	}

	private getDeliveryTimeDateRangeType(): DeliveryTimeDateRangeType {
		const dateRangeOptions = this.deliveryTimeRangeOptions,
			dateRangeFormula = this.model.dateRange;

		let dateRangeType = DeliveryTimeDateRangeType.LAST_WEEK;
		dateRangeOptions.forEach((dateRangeOption: DeliveryTimeRangeOption) => {
			if (dateRangeOption.dateRange.isRangeEqual(dateRangeFormula)) {
				dateRangeType = dateRangeOption.value;
			}
		});

		return dateRangeType;
	}

	override willDestroy() {
		super.willDestroy();
		this.model?.cleanupTemporaryEditDataSelects();
	}

	<template>
		{{#if this.model.isNew}}
			<div class="top-title">
				Schedule weekly updates for a single metric
			</div>
			<NotificationsEditFormMetricSetupRowComponent
				@metricSetup={{this.metricSetup}}
				@isDisabled={{@submitInProgress}}
				@errors={{@errors}}
			/>
		{{/if}}
		<FormRowComponent @label="Frequency" @className="row--radiobuttons">
			<FormRadiobuttonGroupComponent
				class="radiobutton-default"
				{{didUpdate this.checkModelChange @model}}
				@name="deliveryTimeType"
				@selected={{this.selectedDeliveryTimeType}}
				@options={{this.deliveryTimeOptions}}
				@isDisabled={{@submitInProgress}}
				@onChange={{this.changeDeliveryTimeType}}
			/>
		</FormRowComponent>
		<FormRowComponent @label="Every" @className="row--dropdowns-in-line">
			<div class="selector">
				<FormSelectComponent
					name="scheduleDayOfWeek"
					@selected={{this.scheduleDayOfWeek}}
					@options={{this.constantsService.daysOfWeekStrings}}
					@tooltipOnSelectionFromProperty=""
					@showFloatlabel={{false}}
					@isNumeric={{false}}
					@isDisabled={{@submitInProgress}}
					@isRequired={{true}}
					@onChange={{this.changeDaysOfWeek}}
				/>
			</div>

			<div class="selector">
				<label>At</label>
				<FormSelectComponent
					name="time"
					@selected={{this.model.scheduleHourOfDay}}
					@options={{this.constantsService.hoursOfDay}}
					@onChange={{this.changeHourOfDay}}
					@isDisabled={{@submitInProgress}}
					@tooltipOnSelectionFromProperty=""
					@showFloatlabel={{false}}
					@isRequired={{true}}
				/>
			</div>

			<div class="selector">
				<label>Data from</label>
				<FormSelectComponent
					name="dataFrom"
					@selected={{this.selectedDeliveryTimeDateRangeType}}
					@options={{this.deliveryTimeRangeOptions}}
					@onChange={{this.changeDeliveryTimeDateRange}}
					@isDisabled={{@submitInProgress}}
					@tooltipOnSelectionFromProperty=""
					@isRequired={{true}}
				/>
			</div>
		</FormRowComponent>

		<NotificationsDeliveryTargetsComponent
			@selected={{this.model.delivery}}
			@slackChannels={{this.model.slackChannels}}
			@setDefaults={{this.hasDefaultSettings}}
			@isDisabled={{@submitInProgress}}
			@onSelectionChange={{this.updateDeliveryTargets}}
			@isMobileComingSoon={{true}}
			@isSlackComingSoon={{true}}
		>
			<RowErrorComponent @items={{@errors.delivery}} />
		</NotificationsDeliveryTargetsComponent>
	</template>
}
